/* eslint-disable arrow-body-style */
import URI from 'urijs';
import type { Moment } from 'moment';
import * as uuid from 'react-native-uuid';
import { CLIENT_BOOKING_SEARCH_ENTRYPOINT_HAS_SEEN_TOASTER_KEY, CLIENT_BOOKING_SEARCH_ENTRYPOINT_SOURCE } from '../../../components/consumer/booking/BookingFlow/constants';
import { API_ROOT, SEARCH_API_ROOT } from '../../../config';
import FeatureFlags from '../../FeatureFlags';
import { DEFAULT_TRACKING_EVENT_NAME, trackSearch } from './searchHelpers';
import ssFetch from '../../ssFetch';
import loadAvailability, { ILoadAvailabilityResult } from '../../provider/loadAvailability';
import {
  SEARCH_PARAM_RESET_CODE,
  SEARCH_VIRTUAL_APPT_NATIONAL_SEARCH_FLAG_NAME,
  EARNED_PRO_BOOST_FLAG_NAME,
  NEW_PRO_SEARCH_BOOST_FLAG_NAME,
  SEARCH_SERVICES_CLIP_IMAGES_TEST_NAME,
  SEARCH_SERVICES_CLIP_IMAGES_TEST_PERCENT,
  SEARCH_PROS_WITH_AVAILABILITY_FILTER_TEST_NAME,
  SEARCH_PROS_WITH_AVAILABILITY_FILTER_TEST_PERCENT,
  SEARCH_TAILORED_UX_CATEGORIES,
  SEARCH_TAILORED_UX_TEST_NAME,
  SEARCH_TAILORED_UX_TEST_PERCENT,
  SEARCH_V4_TEST_NAME,
  SEARCH_V4_TEST_PERCENT,
  SEARCH_TERM_MATCHING_TEST_NAME,
  SEARCH_TERM_MATCHING_TEST_PERCENT,
  SEARCH_PAYMENTS_ADOPTER_TEST_NAME,
  SEARCH_PAYMENTS_ADOPTER_TEST_PERCENT,
} from './Constants';
import nonCriticalException from '../../exceptionLogger';
import { ITimeSection } from '../../timeSlotUtils';
import type { ImageData } from '../../../components/consumer/search/SearchResult/SearchResultImages/layouts';
import store from '../../../store';
import { Assignment } from '../../../store/ABTest/ABTest.types';
import { CLIENT_SEARCH_HIDE_SERVICES_TEST_NAME, CLIENT_SEARCH_HIDE_SERVICES_TEST_PERCENT } from '../../../components/consumer/search/SearchResult/constants';
import { IHighlightProStrengthsData } from '../../ProSearchMLData/types';
import Responsive from '../../Responsive';
import { getItem } from '../../KeyValueStorage';
import { isPreStage } from '../../env';

const SEARCH_VERSION = 'v3.0';

const SEARCH_URL = `${SEARCH_API_ROOT}/api/${SEARCH_VERSION}/salons.json`;

const DEFAULT_SEARCH_SIZE = 30;

/**
 * Represents a "location" record returned by the search API
 */
interface ILocationData {
  geo_point?: string;
  distance?: number;
  phone_number?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  computed_city?: string;
  computed_county?: string;
  computed_state?: string;
  computed_neighborhood?: string;
}

/**
 * Represents a "salon" record returned by the search API. Salons are the top-most record in search
 * results and contain pros. Many of their properties are copied from pros.
 */
interface ISalonData {
  id: number;
  creation_time: any;
  vanity_url: string;
  name: string;
  blurb?: string;
  summary?: string;
  location?: ILocationData;
  cover_photo?: string;
  num_photos: number;
  num_recommendations: number;
  average_rating: number;
  num_ratings: number;
  num_pro_appointments: number;
  num_client_appointments: number;
  num_total_appointments: number;
  salonified: boolean;
  online_bookable: boolean;
  autocharge_enabled: boolean;
  editorial_tags?: Array<{ name: string, description: string }>;
  groups?: Array<string>;
  mobile_business: boolean;
  search_boost_start_date?: any;
  search_boost_duration_days?: number;
  new_pro_search_boost?: boolean;
  profile_photo?: string;
}

interface IHasAvailableDays {
  available_days: ILoadAvailabilityResult['available_days'];
}

type AtLeastOneService = [IProviderService] & IProviderService[];

/**
 * Represents a provider record within a salon
 */
interface IProviderData extends ISalonData {
  popular_categories: Array<{ category: string, popularity: number }>;
  // always at least 1 service
  matched_services: AtLeastOneService;
  profession?: string;
}

export interface ISearchResultProviderData {
  autocharge_enabled: boolean;
  average_rating: number;
  cover_photo: string;
  creation_time: string;
  groups?: Array<string>;
  id: number;
  matched_salon: ISalonData,
  // always at least 1 service
  matched_services: AtLeastOneService;
  name: string;
  num_client_appointments: number;
  num_photos: number;
  num_pro_appointments: number;
  num_ratings: number;
  num_recommendations: number;
  num_total_appointments: number;
  online_bookable: boolean;
  popular_categories: Array<{ category: string, popularity: number }>;
  profile_photo: string;
  salon_id: number;
  service_ids: number[];
  vanity_url: string;
}

/**
 * Represents the possible values for a query type in a search.
 */
type QueryType = 'name' | 'std_name' | 'pro_name' | 'salon_name' | 'profession' | 'blurb';

/**
 * Represents the metadata returned from the search API along with search results
 */
interface ISearchMetadata {
  total?: number;
  selected_query?: Array<QueryType>;
  matched_queries?: Array<QueryType>;
}

/**
 * Represents a single location record derived from search result data
 */
export interface ILocation extends ILocationData {
  latitude: number;
  longitude: number;
  distance?: number;
}

/**
 * Represents a single service record derived from search result data
 */
export interface IProviderService {
  price: number;
  query_score: number;
  id: number;
  duration: number;
  name: string;
  salon_id: number;
  pro_id: number;
  std_name: string;
  estimated_service_category: string;
  blurb: string;
  popularity_percentage: number;
  openings?: Array<{ date: any, times: string }>;
  matched_queries: Array<QueryType>;
  is_ncd_eligible?: boolean;
  images?: string[];
  clip_images?: Array<ImageData>;
  num_service_images?: number;
  is_online_bookable?: number | boolean;
}

/**
 * Represents a single provider record derived from search result data
 */
export interface IProvider extends IProviderData {
  services: IProviderData['matched_services'],
  popularCategories: IProviderData['popular_categories'],
  location: ILocation,
  // The service with the min price
  matchedService: IProviderService,
}

/**
 * Represents the hair types that a pro specializes in
 */
export interface IProHairTypeData {
  straight: number;
  wavy: number;
  curly: number;
  coily: number;
}

/**
 * Represents a single top-level search result derived from search result data
 */
export interface ISearchResult {
  version: string;
  salon_id: number;
  salon_name: string;
  provider_id: number;
  provider_name: string;
  services: Array<IProviderService>;
  popularCategories: Array<{ category: string, popularity: number }>;
  num_ratings: number;
  average_rating: number;
  isNCDEligible: boolean;
  location: ILocation;
  searchId?: string;
  page?: number;
  index?: number;
  availability?: IHasAvailableDays;
  profile_photo?: string;
  vanity_url?: string;
  profession?: string;
  search_boost_start_date?: Moment;
  search_boost_duration_days?: number;
  nextTimeSlots?: Moment[];
  groups?: string[];
  new_pro_search_boost_end?: string,
  new_pro_search_boost_num_attributed_appointments?: number,
  new_pro_search_boost_start?: string,
  online_bookable?: boolean,
  payments_are_enabled?: number,
  ncd_enabled?: number,
  pro_ab_test_data?: { atl3804?: IProHairTypeData },
  proStrengthHighlightData?: IHighlightProStrengthsData,
  matchedService?: IProviderService,
  matched_services: IProviderService[];
}

/**
 * Represents search metadata returned by the search API and a few additional attributes added by
 * each search instance
 */
export interface IMetadata extends ISearchMetadata {
  origin?: string;
  source?: string;
  page?: number;
  id?: string;
  version?: string;
  trackingEventName?: string;
  trackingFrom?: number;
  new_pro_search_boost_weight?: number;
  new_pro_search_boost?: boolean;
  is_search_v4_query?: boolean;
  search_term_matching_test_dt84?: boolean;
  payments_adopter_test_dp109?: boolean;
}

export interface ISearchResponse {
  meta: IMetadata,
  results: ISearchResultProviderData[],
}

/**
 * Parses a latitude and longitude in a search result from the search API.
 * @param {Object} providerData The result record from the search API to parse
 * @return {Object} An object with `latitude` and `longitude` keys
 */
const transformLocationData = (locationData: ILocationData): ILocation => {
  const geoPoint = locationData.geo_point || '';
  let latLng = geoPoint.split(',')
    .map(coordinate => Number(coordinate));

  if (latLng.length !== 2) {
    latLng = [
      0,
      0,
    ];
  }

  return {
    latitude: latLng[0],
    longitude: latLng[1],
    ...locationData,
  };
};

const transformHighlightProStrengthsData = (
  results: any[], searchTermAsKey: string,
): ISearchResult[] => {
  return results.map(result => {
    if (result.pro_ab_test_data?.atl3954) {
      const item = result.pro_ab_test_data.atl3954;
      return {
        ...result,
        proStrengthHighlightData: {
          searchCategory: item[searchTermAsKey],
          location: item.location,
          hairType: item.hair_type,
        },
      };
    }

    return result;
  });
};

/**
 * Parses a provider in a search result from the search API. Converts properties to camel-case and
 * determines some helpful min/max properties.
 * @param {Object} providerData The result record from the search API to parse
 * @return {Object} The normalized provider data
 */
const transformProviderData = (providerData: IProviderData): IProvider => {
  const initialService: IProviderService = {
    price: 0,
    query_score: 0,
    id: 0,
    duration: 0,
    name: '',
    std_name: '',
    salon_id: 0,
    pro_id: 0,
    estimated_service_category: '',
    blurb: '',
    popularity_percentage: 0,
    matched_queries: [],
  };
  const provider = {
    ...providerData,
    services: providerData.matched_services,
    popularCategories: providerData.popular_categories,
    groups: providerData.groups || [],
    location: transformLocationData(providerData.location || {}),
    // Find the service with the min price
    matchedService: providerData.matched_services.reduce(
      (minService, service) => {
        return (!minService.price || service.price < minService.price) ? service : minService;
      },
      initialService,
    ),
  };

  return provider;
};

export const transformProSearchData = (
  searchResult: ISearchResultProviderData,
): ISearchResult => {
  const {
    matched_salon: salon,
  } = searchResult;
  let provider: IProvider = null;
  let isNCDEligible: boolean = false;

  provider = transformProviderData({
    ...searchResult,
    salonified: salon.salonified,
    mobile_business: salon.mobile_business,
  });
  isNCDEligible = searchResult.matched_services.some(s => s.is_ncd_eligible);

  return {
    ...provider,
    provider_id: provider.id,
    provider_name: provider.name,
    salon_id: salon.id,
    salon_name: salon.name,
    version: SEARCH_VERSION,
    location: transformLocationData(salon.location),
    isNCDEligible,
  };
};

export const recordProSearchAppearance = (proIds = []) => ssFetch(`${API_ROOT}/api/v1/providers/record-search-appearance`, {
  method: 'POST',
  body: { proIds },
});

export type SearchMeetingType = 'nonvirtual' | 'mobile' | 'virtual';

export interface ISearchParameters {
  query?: string;
  lat: number;
  lon: number;
  from?: number;
  size?: number;
  top_left_lat?: number;
  top_left_lon?: number;
  bottom_right_lat?: number;
  bottom_right_lon?: number;
  boost_weight?: number;
  new_pro_search_boost?: boolean;
  ignore_std_name?: boolean;
  inactive_pro_groups?: Array<string>;
  meeting_type?: SearchMeetingType;
  date?: any;
}

export interface ISearchConfig {
  bottomRightLat?: number;
  bottomRightLon?: number;
  topLeftLat?: number;
  topLeftLon?: number;
  lon: number;
  lat: number;
  query: string;
  date: string;
  page?: number;
  from?: number;
  size?: number;
  meetingType?: SearchMeetingType;
  virtual_location_exempt?: boolean;
  boost_weight?: number;
  new_pro_search_boost?: boolean;
  timeSections?: ITimeSection[];
  sort?: string;
  service_images_v2?: boolean;
  has_availability_atl3860?: boolean;
  search_tailored_ux_test_atl3958?: boolean;
  is_search_v4_query?: boolean;
  search_term_matching_test_dt84?: boolean;
  payments_adopter_test_dp109?: boolean;
}

/**
 * Determines whether to fetch availability for search results or not
 * This is a performance optimization because availability is slow
 * Only enable availability in completely necessary circumstances
 * @param date String The date to filter by
 * @returns boolean whether or not to load availability
 */
const shouldRequestAvailability = async (
  date?: string,
  meta?: IMetadata,
) => {
  /**
   * If you need to request availability for search results for a test,
   * Add the check for test here like so:
   *
   *  const inAvailabilityRequiringTest = store.getState().abTest.assignments[flag_name]?.inTest;
   *  return inAvailabilityRequiringTest || !!date;
   */
  // We need availability if there is a date filter applied
  let result = !!date;

  // We also need availability for the booking openings toaster
  if (meta?.source === CLIENT_BOOKING_SEARCH_ENTRYPOINT_SOURCE) {
    const hasSeenSearchEntry = await getItem(CLIENT_BOOKING_SEARCH_ENTRYPOINT_HAS_SEEN_TOASTER_KEY);
    result = !hasSeenSearchEntry || result;
  }

  return result;
};

/**
 * Loads the appropriate availability for the given salons on the given date.
 * @param {Array<Salon>} salons An array of salons to filter by availability.
 * @param {String} [date=null] The date for which to find availability, in YYYY-MM-DD format. If
 * this is falsy, 14 days of availability is loaded
 * @yields {Array<Salon>} The salons with availability in matched_pros/provider keys.
 *        Note this return value could be an empty array!!
 */
export const applyResultAvailability = async (
  salons: ISearchResult[] = [],
  date: string = '',
  // number of days after start date to fetch
  numberOfDays: number = 14,
  meta?: IMetadata,
): Promise<ILoadAvailabilityResult[]> => {
  if (!(await shouldRequestAvailability(date, meta))) {
    return Promise.resolve([]);
  }

  // TODO productize date time filter test ATL-3862
  const inDateTimeFilterTest = Responsive.is.mobile || Responsive.is.tablet;

  return Promise.all(salons.map(async (
    record: ISearchResult & { matchedService: IProviderService },
  ): Promise<ILoadAvailabilityResult> => {
    const providerId = record.provider_id;
    let services: IProviderService[] = [];
    // choose a service to find availability for
    let chosenService: IProviderService = null;
    let serviceDuration: number = -Infinity;
    services = record.services;

    if (inDateTimeFilterTest) {
      // use matched service
      const { matchedService } = record;
      chosenService = matchedService;
      serviceDuration = matchedService.duration;
    } else {
      // choose service with longest availability
      services?.forEach(service => {
        if (service.duration > serviceDuration) {
          chosenService = service;
          serviceDuration = service.duration;
        }
      });
    }

    const availabilityParams: {
      days: number;
      startDate?: string
    } = {
      days: numberOfDays,
    };
    if (!!date || date === SEARCH_PARAM_RESET_CODE) {
      availabilityParams.days = inDateTimeFilterTest ? numberOfDays : 1;
      availabilityParams.startDate = date;
    }

    // get availability for the longest service for just the provided date
    const availability: ILoadAvailabilityResult = await loadAvailability(
      providerId,
      [chosenService],
      availabilityParams,
    );

    return availability;
  }));
};

/**
 * Represents the results of a single call to the search API, including results and metadata.
 * Provides helper functions.
 */
export default class Search {
  results: Array<ISearchResult>;

  id: string;

  filters: ISearchConfig;

  meta: IMetadata;

  /**
   * The config we use on the front end does not map directly to the query parameters expected by
   * the search API. This function is responsible for creating the mapping.
   *
   * @name buildSearchParameters
   * @param {Object} config The search configuration. Any properties supplied get sent directly to
   * the search API as query parameters.
   * @param {Object} [config.mapBoundingBox] The bounding box for search results. Should contain
   * the following properties: `bottomRightLat`, `bottomRightLng`, `topLeftLat`, `topLeftLng`
   * @param {Number} config.lon The longitude around which to search.
   * @param {Number} config.lat The latitude around which to search.
   * @param {String} config.query The query string.
   * @param {String} config.date The date to search on (YYYY-MM-DD format)
   * @param {String} config.timeSections Select time sections as defined in timeUtils TIME_SECTIONS
   * @param {Number} [config.page=0] The zero-index page number to fetch
   * @param {Number} [config.from=0] The result to start the search at.
   * @param {Number} [config.size=30] The number of search results to retrieve.
   * @returns {Object} - The search API query parameters.
   */
  static buildSearchParameters(config: ISearchConfig): ISearchParameters {
    let {
      bottomRightLat,
      bottomRightLon,
      topLeftLat,
      topLeftLon,
      from,
      size,
      // eslint-disable-next-line prefer-const
      ...restConfig
    } = config;
    const {
      meetingType,
      page,
      ...remainingConfig
    } = restConfig;

    // sanitize these inputs a bit
    if (bottomRightLat === null) {
      bottomRightLat = undefined;
    }

    if (bottomRightLon === null) {
      bottomRightLon = undefined;
    }

    if (topLeftLat === null) {
      topLeftLat = undefined;
    }

    if (topLeftLon === null) {
      topLeftLon = undefined;
    }

    if (typeof size !== 'number') {
      size = DEFAULT_SEARCH_SIZE;
    }

    if (typeof from !== 'number') {
      if (typeof page === 'number') {
        from = page * size;
      } else {
        from = 0;
      }
    }

    const cfg: ISearchParameters = {
      ...remainingConfig,
      bottom_right_lat: bottomRightLat,
      bottom_right_lon: bottomRightLon,
      top_left_lat: topLeftLat,
      top_left_lon: topLeftLon,
      meeting_type: meetingType,
      date: config.date || null,
      from,
      size,
    };

    return cfg;
  }

  /**
   * Use this function to generate a brand new search id
   *
   * @returns {String} a search id
   */
  static generateSearchID(): string {
    return `SEARCH-${uuid.v4()}`;
  }

  /**
   * Performs a search for providers. Use this function to begin a search "session".
   *
   * @name create
   * @param {Object} searchConfig The search configuration.
   * @param {Object} [meta] Metadata to attach to the resulting object.
   * @returns {Promise<Search>} A promise which resolves with the search results.
   */
  static async create(searchConfig: ISearchConfig, meta?: IMetadata) {
    if (!Search.canPerformSearch(searchConfig)) {
      const error = isPreStage() && new Error(
        `Don't have enough parameters to call Search API ${JSON.stringify(searchConfig)}`,
      );
      return Promise.reject(error);
    }

    let searchParameters;

    type WithAvailability =
      {
        meta: ISearchResponse['meta'],
        results: Array<ISearchResultProviderData & { availability?: ILoadAvailabilityResult }>
      };

    return FeatureFlags.areEnabled([
      SEARCH_VIRTUAL_APPT_NATIONAL_SEARCH_FLAG_NAME,
      EARNED_PRO_BOOST_FLAG_NAME,
      NEW_PRO_SEARCH_BOOST_FLAG_NAME,
    ])
      .then(async flagValues => {
        const obj = await store.dispatch.abTest.assignTest({
          name: SEARCH_SERVICES_CLIP_IMAGES_TEST_NAME,
          percent: SEARCH_SERVICES_CLIP_IMAGES_TEST_PERCENT,
        }) as any;
        return [
          flagValues,
          obj.inTest,
        ];
      })
      .then(async ([flagValues, isInPhotoRankingTest]) => {
        const obj = await store.dispatch.abTest.assignTest({
          name: SEARCH_PROS_WITH_AVAILABILITY_FILTER_TEST_NAME,
          percent: SEARCH_PROS_WITH_AVAILABILITY_FILTER_TEST_PERCENT,
        }) as unknown as Assignment;
        return [
          flagValues,
          isInPhotoRankingTest,
          obj.inTest,
        ];
      })
      .then(async ([
        flagValues, isInPhotoRankingTest, isInAvailabilityFilterTest,
      ]) => {
        if (!Responsive.is.mobile && !Responsive.is.tablet) {
          return [
            flagValues,
            isInPhotoRankingTest,
            isInAvailabilityFilterTest,
            false,
          ];
        }

        const matched = SEARCH_TAILORED_UX_CATEGORIES.find(([regex]) => {
          return searchConfig.query.match(regex);
        });

        if (!matched) {
          return [
            flagValues,
            isInPhotoRankingTest,
            isInAvailabilityFilterTest,
            false,
          ];
        }

        const obj = await store.dispatch.abTest.assignTest({
          name: SEARCH_TAILORED_UX_TEST_NAME,
          percent: SEARCH_TAILORED_UX_TEST_PERCENT,
        }) as unknown as Assignment;

        return [
          flagValues,
          isInPhotoRankingTest,
          isInAvailabilityFilterTest,
          obj.inTest,
        ];
      })
      .then(async ([
        flagValues,
        isInPhotoRankingTest,
        isInAvailabilityFilterTest,
        isInSearchTailoredUXTest,
      ]) => {
        const obj = await store.dispatch.abTest.assignTest({
          name: SEARCH_V4_TEST_NAME,
          percent: SEARCH_V4_TEST_PERCENT,
        }) as any;
        return [
          flagValues,
          isInPhotoRankingTest,
          isInAvailabilityFilterTest,
          isInSearchTailoredUXTest,
          obj.inTest,
        ];
      })
      .then(async ([
        flagValues,
        isInPhotoRankingTest,
        isInAvailabilityFilterTest,
        isInSearchTailoredUXTest,
        isInSearchV4Test,
      ]) => {
        const obj = await store.dispatch.abTest.assignTest({
          name: SEARCH_TERM_MATCHING_TEST_NAME,
          percent: SEARCH_TERM_MATCHING_TEST_PERCENT,
        }) as any;
        return [
          flagValues,
          isInPhotoRankingTest,
          isInAvailabilityFilterTest,
          isInSearchTailoredUXTest,
          isInSearchV4Test,
          obj.inTest,
        ];
      })
      .then(async ([
        flagValues,
        isInPhotoRankingTest,
        isInAvailabilityFilterTest,
        isInSearchTailoredUXTest,
        isInSearchV4Test,
        isInSearchTermMatchTest,
      ]) => {
        const obj = await store.dispatch.abTest.assignTest({
          name: SEARCH_PAYMENTS_ADOPTER_TEST_NAME,
          percent: SEARCH_PAYMENTS_ADOPTER_TEST_PERCENT,
        }) as any;
        return [
          flagValues,
          isInPhotoRankingTest,
          isInAvailabilityFilterTest,
          isInSearchTailoredUXTest,
          isInSearchV4Test,
          isInSearchTermMatchTest,
          obj.inTest,
        ];
      })
      .then(([
        flagValues,
        isInPhotoRankingTest,
        isInAvailabilityFilterTest,
        isInSearchTailoredUXTest,
        isInSearchV4Test,
        isInSearchTermMatchTest,
        isInPaymentsAdopterTest,
      ]) => {
        const virtualLocationExempt = flagValues[SEARCH_VIRTUAL_APPT_NATIONAL_SEARCH_FLAG_NAME];
        const boostWeight = flagValues[EARNED_PRO_BOOST_FLAG_NAME];
        const newProSearchBoost = flagValues[NEW_PRO_SEARCH_BOOST_FLAG_NAME];
        searchParameters = Search.buildSearchParameters({
          ...searchConfig,
          virtual_location_exempt: virtualLocationExempt,
          meetingType: null,
          boost_weight: boostWeight ? 4.0 : 0.0,
          new_pro_search_boost: newProSearchBoost,
          service_images_v2: isInPhotoRankingTest,
          has_availability_atl3860: isInAvailabilityFilterTest,
          search_tailored_ux_test_atl3958: isInSearchTailoredUXTest,
          is_search_v4_query: isInSearchV4Test,
          search_term_matching_test_dt84: isInSearchTermMatchTest,
          payments_adopter_test_dp109: isInPaymentsAdopterTest,
        });

        searchParameters.lat ||= 0;
        searchParameters.lon ||= 0;

        const url = URI(SEARCH_URL).search(searchParameters).toString();
        // clear the headers because the search API gets confused with the ones we send by default
        const params = { headers: {} };
        return ssFetch(url, params);
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`${response.status}`);
        }

        return response.json() as unknown as ISearchResponse;
      })
      .then(json => {
        if (json.results.length > 0) {
          store.dispatch.abTest.assignTest({
            name: CLIENT_SEARCH_HIDE_SERVICES_TEST_NAME,
            percent: CLIENT_SEARCH_HIDE_SERVICES_TEST_PERCENT,
          });
        }
        return applyResultAvailability(
          json.results.map(result => transformProSearchData(result)),
          searchParameters.date || null,
          undefined,
          meta,
        ).then((availabilities: ILoadAvailabilityResult[]) => {
          const resultsWithAvailability: Array<(ISearchResultProviderData) & {
            availability?: ILoadAvailabilityResult
          }> = json.results;

          return {
            ...json,
            results: availabilities.length > 0
              ? resultsWithAvailability.map((record, index: number) => ({
                ...record,
                availability: availabilities?.[index],
              })) : resultsWithAvailability,
          };
        });
      })
      .then((json: WithAvailability) => {
        const searchModel = new Search(json.results, searchParameters, {
          ...meta,
          ...json.meta,
        });

        return searchModel;
      })
      .catch(e => {
        nonCriticalException(e);
        throw new Error(`Search API call failed with an error: ${e.message}`);
      })
      .then((search: Search) => {
        const {
          id,
          meta: {
            total,
            source,
            origin,
            selected_query: selectedQuery,
            trackingFrom,
            new_pro_search_boost_weight: newProBoostWeight,
            new_pro_search_boost: newProSearchBoost,
            is_search_v4_query: isInSearchV4Test,
          } = {},

          filters: {
            topLeftLat,
            topLeftLon,
            bottomRightLat,
            bottomRightLon,
            query,
            lat,
            lon,
            meetingType,
            date,
            timeSections,
            sort,
            has_availability_atl3860,
          } = {},
          results = [],
        } = search;

        trackSearch(meta?.trackingEventName || DEFAULT_TRACKING_EVENT_NAME, {
          query,
          results,
          latitude: lat,
          longitude: lon,
          searchID: id,
          total,
          source,
          origin,
          from: trackingFrom || 0,
          meetingType,
          selectedQuery,
          topLeftLat,
          topLeftLon,
          bottomRightLat,
          bottomRightLon,
          date,
          timeSections,
          sort,
          newProBoostWeight,
          newProSearchBoost,
          has_availability_atl3860,
          isInSearchV4Test,
          isInSearchTermMatchTest: searchParameters.search_term_matching_test_dt84,
          isInPaymentsAdopterTest: searchParameters.payments_adopter_test_dp109,
        });

        return Promise.resolve(search);
      });
  }

  /**
   * Returns true if there are enough parameters to perform a search
   *
   * @param {Object} searchConfig The search which is passed to `create`.
   * @return {Boolean}
   */
  static canPerformSearch(searchConfig: ISearchConfig): boolean {
    return (
      (typeof searchConfig.lat === 'number' || typeof searchConfig.lat === 'string')
      && (typeof searchConfig.lon === 'number' || typeof searchConfig.lon === 'string')
      && (typeof searchConfig.query === 'string' && !!searchConfig.query.length)
    );
  }

  /**
   * Constructor. Generally, you will not want to call this function and instead should use the
   * static `create` function or the `nextPage` or `previousPage` functions of an existing `Search`
   * instance.
   * @param {Array<Object>} results The results array.
   * @param {Object} filters The filters for the search.
   * @param {Object} meta The metadata hash.
   */
  constructor(
    results: ISearchResultProviderData[] = [],
    filters: ISearchConfig,
    meta: IMetadata = {},
  ) {
    // Using the from and size data returned from the API, calculate
    // which page of results this is.
    const pageIndex = Math.floor(filters.from / filters.size);
    this.meta = {
      ...meta,
      page: pageIndex,
    };
    this.filters = { ...filters };
    const tempResults = results.map((searchResult, index) => {
      const result = {
        ...searchResult,
        searchId: meta.id,
        page: pageIndex,
        index,
      };

      return transformProSearchData(result as ISearchResultProviderData);
    });
    /*
      ATL-3954: (A part of ATL-3958)
      For first page of results, add highlight pro strengths data
    */
    const matched = SEARCH_TAILORED_UX_CATEGORIES.find(([rx]) => filters.query.match(rx));
    const matchedTerm = matched?.[1].searchKey;
    if (filters.search_tailored_ux_test_atl3958 && pageIndex === 0 && !!matchedTerm) {
      this.results = transformHighlightProStrengthsData(tempResults, matchedTerm);
    } else {
      this.results = tempResults;
    }

    this.id = meta.id;
  }
}
